import { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAppCtx } from "../contexts/AppContext";
import {
    LayoutDashboard,
    Coins,
    Building2,
    Landmark,
    Users,
    FileSpreadsheet,
    FileText,
    StickyNote,
    ArrowDownToLine,
    ArrowUpFromLine,
    ArrowLeftRight,
    ShoppingCart,
    BookOpen,
    BookText,
    Calculator,
    TrendingUp,
    BarChart3,
    UserCog,
    Wallet,
    Receipt,
    Shuffle,
    LogOut,
    ChevronDown,
    UserCircle2,
    Menu,
    X,
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";

const NAV = [
    { section: "Overview", items: [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, mod: "dashboard" }] },
    {
        section: "Masters",
        items: [
            { to: "/entities", label: "Entities", icon: Building2, mod: "entities" },
            { to: "/currencies", label: "Currency Master", icon: Coins, mod: "currencies" },
            { to: "/banks", label: "Bank Accounts", icon: Landmark, mod: "banks" },
            { to: "/partners", label: "Partners", icon: Users, mod: "partners" },
            { to: "/overhead-ledgers", label: "Overhead Ledgers", icon: Wallet, mod: "overhead_ledgers" },
            { to: "/general-ledgers", label: "General Ledgers", icon: BookOpen, mod: "general_ledgers" },
        ],
    },
    {
        section: "Transactions",
        items: [
            { to: "/opening-balances", label: "Opening Balances", icon: FileSpreadsheet, mod: "opening_balances" },
            { to: "/sales-invoices", label: "Sales Invoices", icon: FileText, mod: "sales_invoices" },
            { to: "/credit-notes", label: "Credit Notes", icon: StickyNote, mod: "credit_notes" },
            { to: "/receipts", label: "Receipts (Inwards)", icon: ArrowDownToLine, mod: "receipts" },
            { to: "/payments", label: "Payments", icon: ArrowUpFromLine, mod: "payments" },
            { to: "/contras", label: "Contra (Fund Transfers)", icon: ArrowLeftRight, mod: "contras" },
            { to: "/purchase-invoices", label: "Purchase Invoices", icon: ShoppingCart, mod: "purchase_invoices" },
            { to: "/server-name-mappings", label: "Server Name Mappings", icon: Shuffle, mod: "partners" },
            { to: "/journals", label: "Journal Vouchers", icon: BookText, mod: "journals" },
        ],
    },
    {
        section: "Reporting",
        items: [
            { to: "/monthly-pnl", label: "Monthly COGS / OH", icon: Calculator, mod: "monthly_pnl" },
            { to: "/fx-gain-loss", label: "FX Gain/Loss", icon: TrendingUp, mod: "journals" },
            { to: "/reports", label: "Reports", icon: BarChart3, mod: "reports" },
        ],
    },
];

export default function Layout() {
    const { user, logout, hasPerm } = useAuth();
    const { entities, selectedEntity, setSelectedEntity, selectedSegment, setSelectedSegment } = useAppCtx();
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const segments = [
        { value: "all", label: "All Segments" },
        { value: "Voice", label: "Voice (USD)" },
        { value: "SMS", label: "SMS (EUR)" },
    ];

    const currentEntityLabel =
        selectedEntity === "all"
            ? "All Entities"
            : entities.find((e) => e.id === selectedEntity)?.name || "Select Entity";
    const currentSegmentLabel = segments.find((s) => s.value === selectedSegment)?.label || "All Segments";

    const closeDrawer = () => setDrawerOpen(false);

    return (
        <div className="app-shell flex" data-testid="erp-shell">
            {/* Mobile drawer overlay */}
            {drawerOpen && (
                <div
                    onClick={closeDrawer}
                    className="md:hidden fixed inset-0 bg-black/50 z-40"
                    data-testid="drawer-overlay"
                />
            )}

            {/* Sidebar — fixed drawer on mobile, static column from md: */}
            <aside
                className={`
                    bg-[#0f172a] text-slate-200 flex flex-col transition-transform duration-200
                    fixed md:static inset-y-0 left-0 z-50 md:z-auto
                    ${drawerOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0
                    ${collapsed ? "md:w-16" : "md:w-60"}
                    w-64 md:shrink-0 md:min-h-screen overflow-y-auto md:overflow-y-visible
                `}
                data-testid="sidebar"
            >
                <div className="px-4 py-5 border-b border-white/5 flex items-center gap-2">
                    <div className="w-7 h-7 bg-[#002FA7] flex items-center justify-center font-bold text-white text-sm">
                        T
                    </div>
                    {(!collapsed || drawerOpen) && (
                        <div className="flex-1">
                            <div className="text-[15px] font-semibold tracking-tight text-white">TelecomERP</div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-400">Inter-company</div>
                        </div>
                    )}
                    <button
                        onClick={closeDrawer}
                        className="md:hidden text-slate-300 hover:text-white"
                        data-testid="drawer-close-btn"
                        aria-label="Close menu"
                    >
                        <X size={18} />
                    </button>
                </div>
                <nav className="flex-1 overflow-y-auto py-3" onClick={closeDrawer}>
                    {NAV.map((sec) => {
                        const visible = sec.items.filter((it) => hasPerm(it.mod, "read"));
                        if (!visible.length) return null;
                        return (
                            <div key={sec.section}>
                                {(!collapsed || drawerOpen) && <div className="sidebar-section">{sec.section}</div>}
                                {visible.map((it) => (
                                    <NavLink
                                        key={it.to}
                                        to={it.to}
                                        data-testid={`nav-${it.mod}`}
                                        className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                                    >
                                        <it.icon size={16} />
                                        {(!collapsed || drawerOpen) && <span>{it.label}</span>}
                                    </NavLink>
                                ))}
                            </div>
                        );
                    })}
                    {user?.role === "admin" && (
                        <div>
                            {(!collapsed || drawerOpen) && <div className="sidebar-section">Administration</div>}
                            <NavLink
                                to="/users"
                                data-testid="nav-users"
                                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                            >
                                <UserCog size={16} />
                                {(!collapsed || drawerOpen) && <span>Users & Permissions</span>}
                            </NavLink>
                            <NavLink
                                to="/entity-invoice-settings"
                                data-testid="nav-entity-invoice-settings"
                                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                            >
                                <Receipt size={16} />
                                {(!collapsed || drawerOpen) && <span>Invoice Settings</span>}
                            </NavLink>
                        </div>
                    )}
                </nav>
                <button
                    onClick={() => setCollapsed((c) => !c)}
                    className="hidden md:block text-[11px] uppercase tracking-wider text-slate-400 hover:text-white border-t border-white/5 py-2.5"
                    data-testid="sidebar-collapse-btn"
                >
                    {collapsed ? "›" : "‹ Collapse"}
                </button>
            </aside>

            {/* Main */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Top bar */}
                <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
                    <div className="px-3 sm:px-6 h-14 flex items-center gap-2 sm:gap-4">
                        <button
                            onClick={() => setDrawerOpen(true)}
                            className="md:hidden h-8 w-8 flex items-center justify-center rounded-sm border border-slate-200 text-slate-700 hover:bg-slate-50"
                            data-testid="drawer-open-btn"
                            aria-label="Open menu"
                        >
                            <Menu size={16} />
                        </button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="rounded-sm h-8 text-[13px] gap-2 max-w-[55vw] sm:max-w-none truncate"
                                    data-testid="entity-selector-trigger"
                                >
                                    <Building2 size={14} />
                                    <span className="truncate">{currentEntityLabel}</span>
                                    <ChevronDown size={14} />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="rounded-sm">
                                <DropdownMenuLabel>Entity</DropdownMenuLabel>
                                <DropdownMenuItem
                                    onClick={() => setSelectedEntity("all")}
                                    data-testid="entity-option-all"
                                >
                                    All Entities
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {entities.map((e) => (
                                    <DropdownMenuItem
                                        key={e.id}
                                        onClick={() => setSelectedEntity(e.id)}
                                        data-testid={`entity-option-${e.code}`}
                                    >
                                        <span className="text-[10px] tracking-widest font-mono mr-2 text-slate-500">
                                            {e.code}
                                        </span>
                                        {e.name}
                                        <span className="ml-2 text-[11px] text-slate-400">— {e.country}</span>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="rounded-sm h-8 text-[13px] gap-2 hidden sm:inline-flex"
                                    data-testid="segment-selector-trigger"
                                >
                                    <span
                                        className={`w-2 h-2 rounded-full ${selectedSegment === "Voice" ? "bg-sky-500" : selectedSegment === "SMS" ? "bg-violet-500" : "bg-slate-400"}`}
                                    />
                                    {currentSegmentLabel}
                                    <ChevronDown size={14} />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="rounded-sm">
                                {segments.map((s) => (
                                    <DropdownMenuItem
                                        key={s.value}
                                        onClick={() => setSelectedSegment(s.value)}
                                        data-testid={`segment-option-${s.value}`}
                                    >
                                        {s.label}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <div className="flex-1" />

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    className="rounded-sm h-8 gap-2 text-[13px]"
                                    data-testid="user-menu-trigger"
                                >
                                    <div className="w-7 h-7 bg-[#002FA7] text-white text-xs flex items-center justify-center rounded-sm">
                                        {(user?.name || "U").slice(0, 1).toUpperCase()}
                                    </div>
                                    <div className="text-left leading-tight hidden sm:block">
                                        <div className="text-[12.5px] font-medium">{user?.name}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-slate-500">
                                            {user?.role}
                                        </div>
                                    </div>
                                    <ChevronDown size={14} className="hidden sm:inline" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-sm">
                                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    data-testid="profile-menu-item"
                                    onClick={() => navigate("/profile")}
                                >
                                    <UserCircle2 size={14} className="mr-2" /> My profile
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    data-testid="logout-btn"
                                    onClick={async () => {
                                        await logout();
                                        navigate("/login");
                                    }}
                                >
                                    <LogOut size={14} className="mr-2" /> Sign out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                <main className="p-3 sm:p-6 max-w-[1600px] w-full mx-auto" data-testid="main-content">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
